import React from "react";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { TextField } from "@mui/material";
import { Filter, GlobalSearchSelectOption } from "data/@types/GlobalSearchData";
import { useAdvancedViewFiltersStore } from "data/store/global-search/AdvancedViewFiltersStore";
import { FilterOperator } from "./OperatorSelect";
import AppIcon from "ui/components/icons/AppIcon";
import { iconExpandMore } from "ui/components/icons";

const SelectChevronIcon = () => (
  <AppIcon
    icon={iconExpandMore}
    fontSize="small"
    sx={{ mr: 1, pointerEvents: "none" }}
  />
);

type Props = {
  filter: Filter;
  activityType: GlobalSearchSelectOption;
};

const NUMERIC_OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "does_not_equal", label: "Does not equal" },
  { value: "in", label: "In (comma-separated)" },
] as const;

export const NumericIdFilter = ({ filter, activityType }: Props) => {
  const activeFilters = useAdvancedViewFiltersStore(
    (state) => state.filtersByActivity[activityType] || {},
  );
  const setActiveFilters = useAdvancedViewFiltersStore(
    (state) => state.setFiltersForActivity,
  );

  const currentOperator = (activeFilters[filter.label]?.operator ??
    "equals") as FilterOperator;
  const isIn = currentOperator === "in";

  const handleOperatorChange = (event: SelectChangeEvent) => {
    setActiveFilters(activityType, {
      ...activeFilters,
      [filter.label]: {
        ...activeFilters[filter.label],
        operator: event.target.value as FilterOperator,
        valueText: "",
      },
    });
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = isIn
      ? e.target.value.replaceAll(/[^\d, ]/g, "")
      : e.target.value.replaceAll(/\D/g, "");

    setActiveFilters(activityType, {
      ...activeFilters,
      [filter.label]: {
        ...activeFilters[filter.label],
        operator: currentOperator,
        valueText: sanitized,
      },
    });
  };

  return (
    <>
      <Box sx={{ minWidth: 120 }}>
        <FormControl fullWidth>
          <InputLabel id={`${filter.label}-operator-label`}>
            Operator
          </InputLabel>
          <Select
            sx={{ my: 1 }}
            labelId={`${filter.label}-operator-label`}
            size="small"
            value={currentOperator}
            label="Operator"
            onChange={handleOperatorChange}
            IconComponent={SelectChevronIcon}
          >
            {NUMERIC_OPERATORS.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TextField
        size="small"
        label={isIn ? `${filter.label} (e.g. 1, 2, 3)` : filter.label}
        fullWidth
        value={activeFilters[filter.label]?.valueText || ""}
        inputProps={{ inputMode: "numeric" }}
        onChange={handleValueChange}
      />
    </>
  );
};
